// Operator (superAdmin) helpers: search providers and trigger the invite-provider
// Edge Function. RLS allows superAdmin SELECT-all on provider_profiles, so the
// search returns every match; a non-superAdmin would only see their own row,
// which is the correct fail-closed behaviour.

import { supabase } from './supabase';
import { publicRedirect } from './canonical';

const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

export interface ProviderResult {
  id: string;
  user_id: string;
  business_name: string;
  business_type: string | null;
  email: string | null;
  logo_url: string | null;
}

export async function searchProviders(query: string): Promise<ProviderResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  // Escape PostgREST `or` filter metacharacters (commas/parens) in user input.
  const safe = q.replace(/[,()*]/g, ' ');
  const { data, error } = await supabase
    .from('provider_profiles')
    .select('id, user_id, business_name, business_type, email, logo_url')
    .or(`business_name.ilike.%${safe}%,email.ilike.%${safe}%`)
    .order('business_name', { ascending: true })
    .limit(25);
  if (error) throw error;
  return (data ?? []) as ProviderResult[];
}

export interface InviteResult {
  ok: true;
  sent_to: string;
  provider_name: string;
}

export async function sendInvite(
  providerUserId: string,
  overrideEmail?: string,
): Promise<InviteResult> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session expired — sign in again.');

  const resp = await fetch(`${FUNCTIONS_BASE}/invite-provider`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      provider_user_id: providerUserId,
      override_email: overrideEmail || undefined,
      redirect_to: publicRedirect(),
    }),
  });
  const body = (await resp.json().catch(() => ({}))) as Partial<InviteResult> & { error?: string };
  if (!resp.ok || !body.ok) throw new Error(body.error ?? `Invite failed (${resp.status}).`);
  return body as InviteResult;
}

// ── WP2: claim-first operator surfaces ─────────────────────────────

export interface AdminProviderRow {
  id: string;
  business_name: string;
  business_type: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  claimed_at: string | null;
  has_hours: boolean;
  has_logo: boolean;
  has_cover: boolean;
  has_photos: boolean;
  has_phone: boolean;
  has_booking: boolean;
  menus_total: number;
  menus_unreviewed: number;
  sos_eligible: boolean;
  open_code_count: number;
}

/**
 * Operator completeness table.
 *
 * SECURITY DEFINER server-side and superAdmin-gated inside the function, so a
 * non-operator gets a Postgres insufficient_privilege error rather than a
 * filtered list. It has to be a definer function: provider_menus has no
 * superAdmin SELECT policy, so the menu columns are invisible through the table.
 */
export async function adminListProviders(query?: string): Promise<AdminProviderRow[]> {
  const { data, error } = await supabase.rpc('admin_list_providers', {
    p_query: query && query.trim().length > 0 ? query.trim() : null,
    p_limit: 200,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as AdminProviderRow[];
}

export interface IssuedCode {
  ok: boolean;
  code: string;
  profile_id: string;
  business_name: string;
  expires_at: string;
}

export async function issueClaimCode(profileId: string, ttlDays = 30): Promise<IssuedCode> {
  const { data, error } = await supabase.rpc('issue_provider_claim_code', {
    p_profile_id: profileId,
    p_ttl_days: ttlDays,
  });
  if (error) throw new Error(error.message);
  return data as IssuedCode;
}

export interface ApplicationRow {
  id: string;
  business_name: string;
  provider_type: string | null;
  contact_email: string | null;
  user_id: string | null;
  status: string | null;
  created_at: string | null;
}

/** Pending applications. Plain select — superAdmins have an RLS read policy here. */
export async function listPendingApplications(): Promise<ApplicationRow[]> {
  const { data, error } = await supabase
    .from('provider_applications')
    .select('id, business_name, provider_type, contact_email, user_id, status, created_at')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as ApplicationRow[];
}

/**
 * Approve or reject an application.
 *
 * Approving flips status to 'approved', which fires
 * sync_provider_profile_on_approval and materialises the provider_profile. Since
 * WP1 that profile is UNCLAIMED unless the applicant was signed in when they
 * applied — so the operator still has to issue a claim code for anyone who
 * applied anonymously.
 */
export async function setApplicationStatus(
  id: string,
  status: 'approved' | 'rejected',
): Promise<void> {
  const { error } = await supabase
    .from('provider_applications')
    .update({ status, reviewed_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
